import { Children, Fragment, ReactNode } from "react";

import { Button } from "../../design-system/button";

/** A titled card of the form; `action` sits opposite the title, e.g. Edit. */
export const FormSection = ({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) => (
  <section className="tw-flex tw-w-full tw-flex-col tw-gap-[16px] tw-rounded-[16px] tw-border tw-border-solid tw-border-verse-border-primary tw-p-[16px]">
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-[16px]">
      <h2 className="tw-m-0 tw-font-sohne tw-text-[18px] tw-font-light tw-leading-[1.4] tw-tracking-[-0.18px] tw-text-verse-text-primary">
        {title}
      </h2>
      {action}
    </div>
    {children}
  </section>
);

/** The Edit button of a section shown read-only. */
export const EditButton = ({
  section,
  onClick,
}: {
  section: string;
  onClick: () => void;
}) => (
  <Button
    type="button"
    styleType="outline"
    colorScheme="neutral"
    size="sm"
    aria-label={`Edit ${section}`}
    onClick={onClick}
  >
    Edit
  </Button>
);

/** A label and value in a read-only section; `children` go at the row's end. */
export const ReadOnlyRow = ({
  label,
  value,
  children,
}: {
  label: string;
  value: string;
  children?: ReactNode;
}) => (
  <div className="tw-flex tw-w-full tw-items-start tw-gap-[16px] tw-font-sohne tw-text-[14px] tw-leading-[1.4]">
    <span className="tw-w-[160px] tw-shrink-0 tw-text-verse-text-tertiary">
      {label}
    </span>
    <span className="tw-min-w-0 tw-flex-1 tw-text-verse-text-primary">
      {value}
    </span>
    {children}
  </div>
);

/** Label-and-box rows with a divider between them, like the design's details rows. */
export const DetailRows = ({ children }: { children: ReactNode }) => (
  <div className="tw-flex tw-flex-col tw-gap-[8px]">
    {Children.toArray(children).map((row, index) => (
      <Fragment key={index}>
        {index > 0 && (
          <hr className="tw-m-0 tw-w-full tw-border-0 tw-border-t tw-border-solid tw-border-verse-border-primary" />
        )}
        {row}
      </Fragment>
    ))}
  </div>
);

/** A grey group of related fields. */
export const FieldGroup = ({ children }: { children: ReactNode }) => (
  <div className="tw-flex tw-flex-col tw-gap-[16px] tw-rounded-[16px] tw-bg-verse-surface-secondary tw-p-[16px]">
    {children}
  </div>
);

/** A field the design draws narrower than the group. */
export const NarrowField = ({ children }: { children: ReactNode }) => (
  <div className="tw-w-[240px] tw-max-w-full">{children}</div>
);
